import {useForm, Controller} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useSnackbar} from '../../context/SnackbarContextProvider';
import {useMutation} from '@tanstack/react-query';
import AppTextInput from '../../components/common/AppTextInput';
import AppButton from '../../components/common/AppButton';
import {SVG} from '../../constants/assetLists';
import {useNavigate} from 'react-router';
import {handleResetUserPasswordService} from '../../services/users';

type FormValues = {
  email: string;
};

const zodSchema = z.object({
  email: z.email('כתובת מייל לא תקינה').nonempty('שדה חובה!'),
});

const ForgotPassword = () => {
  const {showSnackbar} = useSnackbar();

  const navigate = useNavigate();

  const {
    control,
    watch,
    handleSubmit,
    formState: {errors},
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(zodSchema),
    defaultValues: {
      email: '',
    },
  });

  const email = watch('email');

  const {mutate, isPending} = useMutation({
    mutationFn: (payload: {email: string}) =>
      handleResetUserPasswordService(payload),
    onSuccess: data => {
      showSnackbar({
        type: 'success',
        message: data?.message ?? 'קישור לאיפוס הסיסמה נשלח למייל',
      });
      navigate('/');
    },
  });

  const onSubmit = (data: FormValues) => {
    mutate({email: data.email?.toLowerCase()});
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage:
          'radial-gradient(circle, #85FFF8, #e6fffe, #e6fffe, #e6fffe)',
      }}>
      {/* Back to login */}

      <div className="w-[334px]" dir="rtl">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col items-center gap-8">
          {/* Lock icon */}
          <img
            src={SVG.lock}
            alt="lock"
            className="w-[64px] h-[64px] object-contain"
          />

          {/* Title */}
          <h1 className="text-[32px] font-semibold text-darkBlue">
            שכחת סיסמה?
          </h1>

          {/* Description */}
          <p className="text-center text-[18px] text-black font-medium leading-7">
            אין בעיה! הזן את כתובת האימייל שלך ונשלח לך קישור לאיפוס הסיסמה.
          </p>

          {/* Email input */}
          <Controller
            control={control}
            name="email"
            render={({field}) => (
              <AppTextInput
                label="דואל"
                required
                {...field}
                className={`bg-transparent text-[20px] text-darkerGray
                  ${
                    errors.email
                      ? 'border-raspberryRed focus:border-raspberryRed'
                      : 'border-lightDarkGray focus:!border-deepAqua'
                  }
                `}
                labelClassName={`text-[14px] text-darkerGray
                  ${
                    errors.email
                      ? 'group-focus-within:text-raspberryRed'
                      : 'group-focus-within:!text-deepAqua'
                  }
                `}
                error={errors.email?.message}
              />
            )}
          />

          {/* Submit */}
          <AppButton
            variant="blue"
            className="justify-center text-[24px] font-normal w-full"
            py="14px"
            isLoading={isPending}
            disabled={!email}
            type="submit">
            איפוס סיסמה
          </AppButton>
          {/* Support link */}
          <div className="mt-4">
            <span className="text-black font-medium text-[18px]"> לעזרה </span>
            <button
              type="button"
              onClick={() => {}}
              className="text-[18px] underline text-darkBlue hover:opacity-80">
              צור קשר עם תמיכה
            </button>
          </div>
        </form>
      </div>

      {/* Bottom logo */}
      <div className="absolute bottom-[100px]">
        <img
          src={SVG.appLogoIcon}
          alt="brand"
          className="w-[140px] h-6 object-contain"
        />
      </div>
    </div>
  );
};

export default ForgotPassword;
